import { authHeaders, storageScope } from './auth.js';
import { isRequired } from './consent.js';
import { COMMANDS, normalizeAction } from './action.js';
/**
 * Sending a message to POST /api/chat, and keeping the thread_id that ties
 * a visitor's turns into one conversation.
 *
 * thread_id lives in localStorage, not a cookie: this runs inside the
 * widget's own shadow tree on the customer's page, so it's first-party
 * storage (see loader.js's Shadow-DOM-not-iframe reasoning) and survives a
 * page reload, which is the point — losing conversation memory on every
 * navigation would make multi-turn context useless for a visitor reading
 * more than one page.
 */

const THREAD_STORAGE_PREFIX = 'chatbot_thread:';
const ACTIVITY_STORAGE_PREFIX = 'chatbot_thread_at:';

/**
 * After this long without a message, the widget marks where the visitor
 * picks the conversation up again — the same thread, never a new one: a
 * pause is not a second conversation (ADR-0056).
 */
export const RESUME_AFTER_MS = 30 * 60 * 1000;

/**
 * How long the widget waits for a reply before giving up and showing the
 * error message. Longer than the server's own per-message budget (15 s, see
 * ADR-0047) so the server normally answers first — with a reply or a 503 —
 * and this only catches what the server can't: a dropped connection, a
 * proxy that never answers. A normal reply takes 2–5 s.
 */
export const REPLY_TIMEOUT_MS = 20000;

const OPEN_STORAGE_PREFIX = 'chatbot_open:';

function threadKey(siteKey) {
  return THREAD_STORAGE_PREFIX + siteKey + storageScope();
}

function activityKey(siteKey) {
  return ACTIVITY_STORAGE_PREFIX + siteKey + storageScope();
}

export function loadThreadId(siteKey) {
  try {
    return localStorage.getItem(threadKey(siteKey));
  } catch {
    // Private browsing, or storage blocked entirely — a fresh thread every
    // message is a safe degrade, not a reason to break the widget.
    return null;
  }
}

function saveThreadId(siteKey, threadId) {
  try {
    localStorage.setItem(threadKey(siteKey), threadId);
    localStorage.setItem(activityKey(siteKey), String(Date.now()));
  } catch {
    // Same reasoning as loadThreadId — nothing to recover from, nothing to do.
  }
}

/** When the visitor last got a reply in this thread, or 0 if unknown. */
export function lastActivity(siteKey) {
  try {
    return Number(localStorage.getItem(activityKey(siteKey)) || 0);
  } catch {
    return 0;
  }
}

/** The visitor asked for a new conversation: the next message starts one. */
export function forgetThread(siteKey) {
  try {
    localStorage.removeItem(threadKey(siteKey));
    localStorage.removeItem(activityKey(siteKey));
  } catch {
    // Nothing stored, nothing to forget.
  }
}

/**
 * What the person saw in a conversation, to draw it again on this page
 * (ADR-0074): [{ role: 'user'|'bot', text, at, message_id?, vote? }], oldest first. Kept by the
 * server, never in this page's storage. Any failure is an empty list: the
 * panel starts as it did before, never broken.
 */
export async function fetchConversation(apiBase, siteKey, threadId, timeoutMs = 5000) {
  if (!threadId) return [];
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const query = new URLSearchParams({ site_key: siteKey, thread_id: threadId });
    const response = await fetch(`${apiBase}/api/conversation?${query}`, { headers: authHeaders(), signal: controller.signal });
    if (!response.ok) return [];
    const body = await response.json();
    return Array.isArray(body.messages) ? body.messages.filter((m) => (m.role === 'user' || m.role === 'bot') && typeof m.text === 'string') : [];
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Whether the panel was open in this tab, so moving to another page keeps
 * it open (sessionStorage: a new tab does not open it on its own).
 */
export function rememberOpen(siteKey, open) {
  try {
    if (open) sessionStorage.setItem(OPEN_STORAGE_PREFIX + siteKey + storageScope(), '1');
    else sessionStorage.removeItem(OPEN_STORAGE_PREFIX + siteKey + storageScope());
  } catch {
    // Storage blocked: the panel just starts closed.
  }
}

export function wasOpen(siteKey) {
  try {
    return sessionStorage.getItem(OPEN_STORAGE_PREFIX + siteKey + storageScope()) === '1';
  } catch {
    return false;
  }
}

/** Whether a message now continues a thread after a long pause. */
export function isResuming(threadId, lastActivityAt, now = Date.now()) {
  return Boolean(threadId) && lastActivityAt > 0 && now - lastActivityAt > RESUME_AFTER_MS;
}

/**
 * Returns one of:
 * - { kind: 'reply', reply, threadId, handoffOffered, handoffOffer, detectedLocale, messageId, action }
 * - { kind: 'handoff', conversationStatus } — a human already owns this
 *   conversation (409); the caller should switch into live-chat mode once
 *   that exists (chatbot-serverless), not retry.
 * - { kind: 'consent' } — the chatbot asks for an authorization (403
 *   consent_required, ADR-0089): the panel asks the visitor again
 * - { kind: 'session' } — the chat session was refused (401): earn a new
 *   one and send again, once (ADR-0057).
 * - { kind: 'error' } — network failure, timeout, a 503 (no model could
 *   answer) or anything else unexpected. The widget shows one generic
 *   message rather than surfacing internals to a visitor.
 */
export async function sendMessage(apiBase, siteKey, message, threadId, locale, timeoutMs = REPLY_TIMEOUT_MS, session = null, actionCommand = null) {
  const url = new URL('/api/chat', apiBase);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(session ? { 'X-Chat-Session': session } : {}) },
      body: JSON.stringify({
        site_key: siteKey,
        message,
        ...(threadId ? { thread_id: threadId } : {}),
        ...(locale ? { locale } : {}),
        // To answer "what are your hours?" in the visitor's own time (ADR-0066 §7).
        ...(timezone() ? { visitor_timezone: timezone() } : {}),
        // A button of an action's summary card (ADR-0108); `message` is its label.
        ...(COMMANDS.has(actionCommand) ? { action_command: actionCommand } : {}),
      }),
    });
  } catch {
    // Includes the abort from the timer above: from the visitor's side a
    // reply that never came and a network error are the same thing.
    return { kind: 'error' };
  } finally {
    clearTimeout(timer);
  }

  // The session expired or was revoked: the caller earns a new one.
  if (response.status === 401) {
    return { kind: 'session' };
  }
  // A limit (ADR-0093): wait, never earn a new session to get round it.
  if (response.status === 429) {
    return { kind: 'limit' };
  }
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    return { kind: 'handoff', conversationStatus: body.conversation_status };
  }
  // The chatbot asks for an authorization this visitor no longer has (ADR-0089).
  if (response.status === 403 && isRequired(403, await response.json().catch(() => null))) {
    return { kind: 'consent' };
  }
  if (!response.ok) {
    return { kind: 'error' };
  }

  const body = await response.json();
  saveThreadId(siteKey, body.thread_id);
  return {
    kind: 'reply',
    reply: body.reply,
    threadId: body.thread_id,
    handoffOffered: body.handoff_offered,
    // What to offer about a person and the team's state (ADR-0065); an older
    // backend only says handoff_offered.
    handoffOffer: body.handoff_offer || (body.handoff_offered ? { reason: 'insufficient', state: 'available', opens_at: null } : null),
    // The language the visitor wrote in, when the backend could tell (ADR-0063 §7).
    detectedLocale: body.detected_locale || null,
    // For the visitor's 👍/👎 (ADR-0102); an older backend sends none.
    messageId: typeof body.message_id === 'string' ? body.message_id : null,
    // An action under way (ADR-0108); an older backend sends none.
    action: normalizeAction(body.action),
    // The reply gives the WhatsApp number: offer to open it (ADR-0110).
    whatsapp: body.whatsapp === true,
  };
}

function timezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
